// noinspection JSUnusedGlobalSymbols

import * as Apollo from '@apollo/client';
import { gql } from '@apollo/client';
import type * as Types from '../../__generated__/types';
import type {
  EmployeeExternalAccount_EmployeeExternalAccountEloomi_Fragment,
  EmployeeExternalAccount_EmployeeExternalAccountGitHub_Fragment,
  EmployeeExternalAccount_EmployeeExternalAccountGoogle_Fragment,
  EmployeeExternalAccount_EmployeeExternalAccountGreenhouse_Fragment,
  EmployeeExternalAccount_EmployeeExternalAccountHubSpot_Fragment,
  EmployeeExternalAccount_EmployeeExternalAccountSlack_Fragment,
} from './getEmployeeExternalAccount.generated';
import { EmployeeExternalAccountFragmentDoc } from './getEmployeeExternalAccount.generated';

const defaultOptions = {} as const;
export type GetEmployeeQueryVariables = Types.Exact<{
  id: Types.Scalars['ID']['input'];
}>;

export type GetEmployeeQuery = { __typename?: 'Query' } & {
  employee: Types.Maybe<
    { __typename?: 'Employee' } & Pick<
      Types.Employee,
      'id' | 'pictureUrl' | 'status' | 'contractNote' | 'jobHistoryNote' | 'createdAt'
    > & {
        probationReview: Types.Maybe<
          { __typename?: 'ProbationReview' } & Pick<
            Types.ProbationReview,
            | 'probationStatus'
            | 'outcome'
            | 'flags'
            | 'expectation'
            | 'employeeId'
            | 'probationEndDate'
            | 'nextDueDate'
            | 'daysOverdue'
            | 'reviewerAbsent'
          > & {
              extension: Types.Maybe<
                { __typename?: 'ProbationReviewExtensionSummary' } & Pick<
                  Types.ProbationReviewExtensionSummary,
                  'count' | 'originalEndDate' | 'newEndDate' | 'reason'
                >
              >;
              reviews: Array<
                { __typename?: 'ProbationReviewStage' } & Pick<
                  Types.ProbationReviewStage,
                  | 'deadline'
                  | 'reviewerFeedback'
                  | 'reviewerId'
                  | 'selfFeedback'
                  | 'reviewStatus'
                  | 'completionMethod'
                  | 'submittedAt'
                  | 'isExtension'
                  | 'extensionReason'
                  | 'needAssessments'
                > & {
                    reviewer: Types.Maybe<
                      { __typename?: 'Employee' } & Pick<Types.Employee, 'statusBasedOnLastWorkingDate'> & {
                          generalInformation: { __typename?: 'EmployeeGeneralInformation' } & Pick<
                            Types.EmployeeGeneralInformation,
                            'preferredFullName'
                          >;
                        }
                    >;
                    assessments: Array<
                      { __typename?: 'ProbationReviewStageAssessment' } & Pick<
                        Types.ProbationReviewStageAssessment,
                        'type'
                      > & {
                          assessment: { __typename?: 'ProbationReviewStageAssessmentValue' } & Pick<
                            Types.ProbationReviewStageAssessmentValue,
                            'reviewer' | 'self'
                          >;
                        }
                    >;
                  }
              >;
            }
        >;
        managers: Array<
          { __typename?: 'Employee' } & Pick<Types.Employee, 'id' | 'statusBasedOnLastWorkingDate'> & {
              generalInformation: { __typename?: 'EmployeeGeneralInformation' } & Pick<
                Types.EmployeeGeneralInformation,
                'preferredFullName'
              >;
            }
        >;
        permission: Types.Maybe<
          { __typename?: 'EmployeePermission' } & Pick<
            Types.EmployeePermission,
            'id' | 'accessSpecificEmployees' | 'companyIds' | 'employeeIds' | 'role' | 'idpFullAccess'
          > & {
              companies: Array<{ __typename?: 'Company' } & Pick<Types.Company, 'id' | 'name'>>;
              employees: Array<
                { __typename?: 'Employee' } & Pick<Types.Employee, 'id'> & {
                    generalInformation: { __typename?: 'EmployeeGeneralInformation' } & Pick<
                      Types.EmployeeGeneralInformation,
                      'preferredFullName' | 'legalFullName'
                    >;
                    currentMainContract: Types.Maybe<
                      { __typename?: 'EmployeeContract' } & Pick<Types.EmployeeContract, 'id'> & {
                          company: { __typename?: 'Company' } & Pick<Types.Company, 'id' | 'name'>;
                        }
                    >;
                    currentPosition: Types.Maybe<
                      { __typename?: 'EmployeeJobHistory' } & Pick<Types.EmployeeJobHistory, 'id' | 'level'> & {
                          businessUnit: { __typename?: 'BusinessUnit' } & Pick<Types.BusinessUnit, 'id' | 'name'>;
                          department: { __typename?: 'Department' } & Pick<Types.Department, 'id' | 'name'>;
                        }
                    >;
                  }
              >;
            }
        >;
        generalInformation: { __typename?: 'EmployeeGeneralInformation' } & Pick<
          Types.EmployeeGeneralInformation,
          | 'preferredFullName'
          | 'legalFullName'
          | 'nameInLocalLanguage'
          | 'needsProbationReview'
          | 'probationReviewEndDate'
          | 'greenhouseLink'
          | 'japanesePhoneticName'
          | 'isNewGraduate'
          | 'hiredDate'
          | 'hiringSource'
        > & {
            nationality: Types.Maybe<{ __typename?: 'Country' } & Pick<Types.Country, 'id' | 'demonym'>>;
            companyEmail: Types.Maybe<
              { __typename?: 'EmployeeCompanyEmail' } & Pick<
                Types.EmployeeCompanyEmail,
                'domainId' | 'account' | 'fullAddress'
              >
            >;
            primaryManager: Types.Maybe<
              { __typename?: 'Employee' } & Pick<Types.Employee, 'id'> & {
                  generalInformation: { __typename?: 'EmployeeGeneralInformation' } & Pick<
                    Types.EmployeeGeneralInformation,
                    'preferredFullName' | 'legalFullName'
                  >;
                }
            >;
            dottedLineManager: Types.Maybe<
              { __typename?: 'Employee' } & Pick<Types.Employee, 'id'> & {
                  generalInformation: { __typename?: 'EmployeeGeneralInformation' } & Pick<
                    Types.EmployeeGeneralInformation,
                    'preferredFullName' | 'legalFullName'
                  >;
                }
            >;
          };
        personalInformation: Types.Maybe<
          { __typename?: 'EmployeePersonalInformation' } & Pick<
            Types.EmployeePersonalInformation,
            | 'age'
            | 'personalEmail'
            | 'gender'
            | 'dateOfBirth'
            | 'currentAddress'
            | 'permanentAddress'
            | 'residentialCountryId'
            | 'idType'
            | 'idNumber'
            | 'idIssuedDate'
            | 'idIssuedPlace'
            | 'idExpiryDate'
            | 'passportName'
            | 'passportNumber'
            | 'passportIssuedPlace'
            | 'passportIssuedDate'
            | 'passportExpiryDate'
            | 'placeOfBirth'
            | 'taxCode'
            | 'socialInsuranceNumber'
            | 'socialInsuranceNotes'
            | 'pensionVoluntaryContributionRate'
            | 'taxWithholdingRate'
            | 'nhiDependents'
            | 'maritalStatus'
            | 'remarks'
          > & {
              personalPhoneNumber: Types.Maybe<
                { __typename?: 'PhoneNumber' } & Pick<Types.PhoneNumber, 'countryId' | 'number'> & {
                    country: { __typename?: 'Country' } & Pick<Types.Country, 'isdCode'>;
                  }
              >;
              emergencyContacts: Array<
                { __typename?: 'EmployeeEmergencyContact' } & Pick<
                  Types.EmployeeEmergencyContact,
                  'id' | 'employeeId' | 'name' | 'relation' | 'remarks'
                > & {
                    phoneNumber: { __typename?: 'PhoneNumber' } & Pick<Types.PhoneNumber, 'countryId' | 'number'> & {
                        country: { __typename?: 'Country' } & Pick<Types.Country, 'isdCode'>;
                      };
                  }
              >;
              visas: Array<
                { __typename?: 'EmployeeVisa' } & Pick<
                  Types.EmployeeVisa,
                  | 'id'
                  | 'employeeId'
                  | 'visaExpiryDate'
                  | 'visaIssuedDate'
                  | 'visaIssuedPlace'
                  | 'visaNumber'
                  | 'visaType'
                  | 'workPermitExpiryDate'
                  | 'workPermitIssuedDate'
                  | 'workPermitNumber'
                >
              >;
              dependencies: Array<
                { __typename?: 'EmployeeDependent' } & Pick<
                  Types.EmployeeDependent,
                  'id' | 'employeeId' | 'name' | 'relation' | 'dateOfBirth' | 'age'
                >
              >;
              bank: Types.Maybe<
                { __typename?: 'EmployeeBank' } & Pick<
                  Types.EmployeeBank,
                  'accountHolderName' | 'accountNumber' | 'branchName' | 'countryId' | 'name'
                >
              >;
              consultingFeeBank: Types.Maybe<
                { __typename?: 'EmployeeConsultingFeeBank' } & Pick<
                  Types.EmployeeConsultingFeeBank,
                  'accountHolderName' | 'accountNumber' | 'branchName' | 'countryId' | 'currencyId' | 'name'
                >
              >;
            }
        >;
        currentMainContract: Types.Maybe<
          { __typename?: 'EmployeeContract' } & Pick<
            Types.EmployeeContract,
            'id' | 'employeeCode' | 'contractType' | 'effectiveDateUntil'
          > & {
              company: { __typename?: 'Company' } & Pick<Types.Company, 'id' | 'name' | 'countryId'> & {
                  country: { __typename?: 'Country' } & Pick<Types.Country, 'name'>;
                  probationReviewTerm: { __typename?: 'CompanyProbationReviewTerm' } & Pick<
                    Types.CompanyProbationReviewTerm,
                    'length' | 'mode'
                  >;
                };
            }
        >;
        currentPosition: Types.Maybe<
          { __typename?: 'EmployeeJobHistory' } & Pick<
            Types.EmployeeJobHistory,
            'id' | 'level' | 'jobTitle' | 'team' | 'type' | 'departmentLead'
          > & {
              businessUnit: { __typename?: 'BusinessUnit' } & Pick<
                Types.BusinessUnit,
                'id' | 'name' | 'defaultEpmMasterRoleTypeId'
              >;
              department: { __typename?: 'Department' } & Pick<Types.Department, 'id' | 'name'>;
              job: { __typename?: 'Job' } & Pick<Types.Job, 'id' | 'name' | 'defaultEpmMasterFunctionalityId'>;
              location: { __typename?: 'Location' } & Pick<Types.Location, 'id' | 'name'>;
            }
        >;
        offBoarding: Types.Maybe<
          { __typename?: 'EmployeeOffBoarding' } & Pick<
            Types.EmployeeOffBoarding,
            | 'detailReasonForLeaving'
            | 'employmentEndDate'
            | 'lastWorkingDate'
            | 'replacementValue'
            | 'replacementValueBasis'
            | 'rehireRecommendation'
            | 'rehireConditions'
            | 'rehireConditionNote'
          > & {
              reasonForLeaving: Types.Maybe<
                { __typename?: 'ReasonForLeaving' } & Pick<Types.ReasonForLeaving, 'id' | 'name'>
              >;
              doNotDeactivate: { __typename?: 'EmployeeOffBoardingDoNotDeactivate' } & Pick<
                Types.EmployeeOffBoardingDoNotDeactivate,
                'greenhouse' | 'google' | 'eloomi'
              >;
              performanceReviewExclusionStart: Types.Maybe<
                { __typename?: 'PerformanceReviewPeriod' } & Pick<Types.PerformanceReviewPeriod, 'year' | 'quarter'>
              >;
            }
        >;
        jobHistories: Array<
          { __typename?: 'EmployeeJobHistory' } & Pick<
            Types.EmployeeJobHistory,
            | 'id'
            | 'effectiveDate'
            | 'effectiveDateUntil'
            | 'type'
            | 'level'
            | 'locationId'
            | 'businessUnitId'
            | 'departmentId'
            | 'jobId'
            | 'jobTitle'
            | 'team'
            | 'regionalRole'
            | 'departmentLead'
          > & {
              location: { __typename?: 'Location' } & Pick<Types.Location, 'name'> & {
                  country: Types.Maybe<{ __typename?: 'Country' } & Pick<Types.Country, 'name'>>;
                };
              businessUnit: { __typename?: 'BusinessUnit' } & Pick<Types.BusinessUnit, 'name' | 'divisionId'> & {
                  division: { __typename?: 'Division' } & Pick<Types.Division, 'name'>;
                  definitions: Array<
                    { __typename?: 'DefinitionItem' } & Pick<Types.DefinitionItem, 'languageCode' | 'content'>
                  >;
                };
              department: { __typename?: 'Department' } & Pick<Types.Department, 'name'> & {
                  definitions: Array<
                    { __typename?: 'DefinitionItem' } & Pick<Types.DefinitionItem, 'languageCode' | 'content'>
                  >;
                };
              job: { __typename?: 'Job' } & Pick<Types.Job, 'name'> & {
                  definitions: Array<
                    { __typename?: 'DefinitionItem' } & Pick<Types.DefinitionItem, 'languageCode' | 'content'>
                  >;
                };
            }
        >;
        contracts: Array<
          { __typename?: 'EmployeeContract' } & Pick<
            Types.EmployeeContract,
            | 'id'
            | 'companyId'
            | 'contractType'
            | 'effectiveDate'
            | 'effectiveDateUntil'
            | 'employeeCode'
            | 'onSecondment'
            | 'isMain'
            | 'isPreAcquisition'
          > & { company: { __typename?: 'Company' } & Pick<Types.Company, 'name' | 'acquiredDate'> }
        >;
        externalAccounts: Array<
          | ({
              __typename?: 'EmployeeExternalAccountEloomi';
            } & EmployeeExternalAccount_EmployeeExternalAccountEloomi_Fragment)
          | ({
              __typename?: 'EmployeeExternalAccountGitHub';
            } & EmployeeExternalAccount_EmployeeExternalAccountGitHub_Fragment)
          | ({
              __typename?: 'EmployeeExternalAccountGoogle';
            } & EmployeeExternalAccount_EmployeeExternalAccountGoogle_Fragment)
          | ({
              __typename?: 'EmployeeExternalAccountGreenhouse';
            } & EmployeeExternalAccount_EmployeeExternalAccountGreenhouse_Fragment)
          | ({
              __typename?: 'EmployeeExternalAccountHubSpot';
            } & EmployeeExternalAccount_EmployeeExternalAccountHubSpot_Fragment)
          | ({
              __typename?: 'EmployeeExternalAccountSlack';
            } & EmployeeExternalAccount_EmployeeExternalAccountSlack_Fragment)
        >;
        googleProductAndSKUForNew: Types.Maybe<
          { __typename?: 'GoogleProductAndSKUWithDisplayName' } & Pick<
            Types.GoogleProductAndSkuWithDisplayName,
            'value' | 'displayName'
          >
        >;
        performanceReviews: Array<
          { __typename?: 'PerformanceReview' } & Pick<Types.PerformanceReview, 'id'> & {
              period: { __typename?: 'PerformanceReviewPeriod' } & Pick<
                Types.PerformanceReviewPeriod,
                'year' | 'quarter' | 'version' | 'isCurrent'
              > & {
                  effectivePeriod: { __typename?: 'PerformanceReviewPeriodRange' } & Pick<
                    Types.PerformanceReviewPeriodRange,
                    'end' | 'start'
                  >;
                };
              setting: { __typename?: 'PerformanceReviewSetting' } & Pick<Types.PerformanceReviewSetting, 'stage'>;
            }
        >;
      }
  >;
};

export const GetEmployeeDocument = /*#__PURE__*/ gql`
    query getEmployee($id: ID!) {
  employee(id: $id) {
    id
    pictureUrl
    probationReview {
      probationStatus
      outcome
      flags
      expectation
      employeeId
      probationEndDate
      nextDueDate
      daysOverdue
      reviewerAbsent
      extension {
        count
        originalEndDate
        newEndDate
        reason
      }
      reviews {
        deadline
        reviewerFeedback
        reviewerId
        selfFeedback
        reviewStatus
        completionMethod
        submittedAt
        isExtension
        extensionReason
        reviewer {
          statusBasedOnLastWorkingDate
          generalInformation {
            preferredFullName
          }
        }
        assessments {
          assessment {
            reviewer
            self
          }
          type
        }
        needAssessments
      }
    }
    managers {
      id
      statusBasedOnLastWorkingDate
      generalInformation {
        preferredFullName
      }
    }
    permission {
      id
      accessSpecificEmployees
      companyIds
      employeeIds
      role
      idpFullAccess
      companies {
        id
        name
      }
      employees {
        id
        generalInformation {
          preferredFullName
          legalFullName
        }
        currentMainContract {
          id
          company {
            id
            name
          }
        }
        currentPosition {
          id
          businessUnit {
            id
            name
          }
          department {
            id
            name
          }
          level
        }
      }
    }
    generalInformation {
      preferredFullName
      legalFullName
      nameInLocalLanguage
      needsProbationReview
      probationReviewEndDate
      greenhouseLink
      japanesePhoneticName
      nationality {
        id
        demonym
      }
      companyEmail {
        domainId
        account
        fullAddress
      }
      primaryManager {
        id
        generalInformation {
          preferredFullName
          legalFullName
        }
      }
      dottedLineManager {
        id
        generalInformation {
          preferredFullName
          legalFullName
        }
      }
      isNewGraduate
      hiredDate
      hiringSource
    }
    personalInformation {
      age
      personalEmail
      gender
      dateOfBirth
      personalPhoneNumber {
        countryId
        country {
          isdCode
        }
        number
      }
      currentAddress
      permanentAddress
      residentialCountryId
      emergencyContacts {
        id
        employeeId
        name
        phoneNumber {
          countryId
          country {
            isdCode
          }
          number
        }
        relation
        remarks
      }
      idType
      idNumber
      idIssuedDate
      idIssuedPlace
      idExpiryDate
      passportName
      passportNumber
      passportIssuedPlace
      passportIssuedDate
      passportExpiryDate
      placeOfBirth
      visas {
        id
        employeeId
        visaExpiryDate
        visaIssuedDate
        visaIssuedPlace
        visaNumber
        visaType
        workPermitExpiryDate
        workPermitIssuedDate
        workPermitNumber
      }
      taxCode
      socialInsuranceNumber
      socialInsuranceNotes
      pensionVoluntaryContributionRate
      taxWithholdingRate
      nhiDependents
      maritalStatus
      dependencies {
        id
        employeeId
        name
        relation
        dateOfBirth
        age
      }
      bank {
        accountHolderName
        accountNumber
        branchName
        countryId
        name
      }
      consultingFeeBank {
        accountHolderName
        accountNumber
        branchName
        countryId
        currencyId
        name
      }
      remarks
    }
    currentMainContract {
      id
      employeeCode
      contractType
      company {
        id
        name
        countryId
        country {
          name
        }
        probationReviewTerm {
          length
          mode
        }
      }
      effectiveDateUntil
    }
    currentPosition {
      id
      level
      businessUnit {
        id
        name
        defaultEpmMasterRoleTypeId
      }
      department {
        id
        name
      }
      job {
        id
        name
        defaultEpmMasterFunctionalityId
      }
      jobTitle
      level
      location {
        id
        name
      }
      team
      type
      departmentLead
    }
    offBoarding {
      detailReasonForLeaving
      employmentEndDate
      lastWorkingDate
      reasonForLeaving {
        id
        name
      }
      doNotDeactivate {
        greenhouse
        google
        eloomi
      }
      replacementValue
      replacementValueBasis
      rehireRecommendation
      rehireConditions
      rehireConditionNote
      performanceReviewExclusionStart {
        year
        quarter
      }
    }
    jobHistories {
      id
      effectiveDate
      effectiveDateUntil
      type
      level
      locationId
      location {
        name
        country {
          name
        }
      }
      businessUnitId
      businessUnit {
        name
        divisionId
        division {
          name
        }
        definitions {
          languageCode
          content
        }
      }
      departmentId
      department {
        name
        definitions {
          languageCode
          content
        }
      }
      jobId
      job {
        name
        definitions {
          languageCode
          content
        }
      }
      jobTitle
      team
      regionalRole
      departmentLead
    }
    contracts {
      id
      company {
        name
        acquiredDate
      }
      companyId
      contractType
      effectiveDate
      effectiveDateUntil
      employeeCode
      onSecondment
      isMain
      isPreAcquisition
    }
    externalAccounts {
      ...employeeExternalAccount
    }
    googleProductAndSKUForNew {
      value
      displayName
    }
    status
    contractNote
    jobHistoryNote
    createdAt
    performanceReviews(isRecentOnly: false) {
      period {
        year
        quarter
        version
        isCurrent
        effectivePeriod {
          end
          start
        }
      }
      setting {
        stage
      }
      id
    }
  }
}
    ${EmployeeExternalAccountFragmentDoc}`;

/**
 * __useGetEmployeeQuery__
 *
 * To run a query within a React component, call `useGetEmployeeQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetEmployeeQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetEmployeeQuery({
 *   variables: {
 *      id: // value for 'id'
 *   },
 * });
 */
export function useGetEmployeeQuery(
  baseOptions: Apollo.QueryHookOptions<GetEmployeeQuery, GetEmployeeQueryVariables> &
    ({ variables: GetEmployeeQueryVariables; skip?: boolean } | { skip: boolean }),
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useQuery<GetEmployeeQuery, GetEmployeeQueryVariables>(GetEmployeeDocument, options);
}
export function useGetEmployeeLazyQuery(
  baseOptions?: Apollo.LazyQueryHookOptions<GetEmployeeQuery, GetEmployeeQueryVariables>,
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useLazyQuery<GetEmployeeQuery, GetEmployeeQueryVariables>(GetEmployeeDocument, options);
}
// @ts-expect-error
export function useGetEmployeeSuspenseQuery(
  baseOptions?: Apollo.SuspenseQueryHookOptions<GetEmployeeQuery, GetEmployeeQueryVariables>,
): Apollo.UseSuspenseQueryResult<GetEmployeeQuery, GetEmployeeQueryVariables>;
export function useGetEmployeeSuspenseQuery(
  baseOptions?: Apollo.SkipToken | Apollo.SuspenseQueryHookOptions<GetEmployeeQuery, GetEmployeeQueryVariables>,
): Apollo.UseSuspenseQueryResult<GetEmployeeQuery | undefined, GetEmployeeQueryVariables>;
export function useGetEmployeeSuspenseQuery(
  baseOptions?: Apollo.SkipToken | Apollo.SuspenseQueryHookOptions<GetEmployeeQuery, GetEmployeeQueryVariables>,
) {
  const options = baseOptions === Apollo.skipToken ? baseOptions : { ...defaultOptions, ...baseOptions };
  return Apollo.useSuspenseQuery<GetEmployeeQuery, GetEmployeeQueryVariables>(GetEmployeeDocument, options);
}
export type GetEmployeeQueryHookResult = ReturnType<typeof useGetEmployeeQuery>;
export type GetEmployeeLazyQueryHookResult = ReturnType<typeof useGetEmployeeLazyQuery>;
export type GetEmployeeSuspenseQueryHookResult = ReturnType<typeof useGetEmployeeSuspenseQuery>;
